import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";
import api from "../api/client";
import LoadingScreen from "../components/LoadingScreen";
import Card from "../components/Card";
import TabBar from "../components/TabBar";
import Button from "../components/Button";
import TextField from "../components/TextField";
import Table from "../components/Table";
import Tag from "../components/Tag";
import Pagination from "../components/Pagination";
import Dropdown from "../components/Dropdown";
import Modal from "../components/Modal";
import ChartSettings from "../components/ChartSettings";
import DownloadSettings from "../components/DownloadSettings";
import ShareSettings from "../components/ShareSettings";
import ShareEmailModal from "../components/ShareEmailModal";
import SpeciesSettings from "../components/SpeciesSettings.jsx";
import AuthorizationTypeSettings from "../components/AuthorizationTypeSettings";
import DateRangePicker from "../components/DateRangePicker";
import { loadFilterSection, saveFilterSection } from "../utils/filterStorage";
import { getCurrentSeasonDateRange } from "../utils/seasonDateRange";
import { formatNumber } from "../utils/formatNumber";
import proj4 from "proj4";

proj4.defs("EPSG:25830", "+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs");
import "./styles.css";

// Chart.js imports
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
);

// Importar imágenes de animales
import imgCorzo from "../assets/8b43135dbc0c9e16e1496fc50bb35c1676f2b40d.png";
import imgJabali from "../assets/35ee85eb15ac73963fb750c112a09261c36913b2.png";
import imgCabraMontes from "../assets/93300cd283de98250983c5cb1c9d7f0c35be5f26.png";
import imgCiervo from "../assets/8c8152eb2021b80f4880dee496903dade728006a.png";
import imgPalomaTorcaz from "../assets/d927dbbe0e85a2418fdc50ab89ee52120f52f9ce.png";
import imgMuflon from "../assets/d927dbbe0e85a2967834hdbaokcui37889ee52120f52f9ce.png";
import imgGamo from "../assets/d927dbbe0e85a2418fdc50ab89578120f52f9de.png";
import imgRebeco from "../assets/d927dbbe0e832k23b95n3lab89ee52120f52f9ce.png";
import imgTortola from "../assets/d927dbbe0e832349u8d95n3lab89ee52120f52f9ce.png";
import imgCodorniz from "../assets/img/especies/Codorniz.png";

// Importar iconos
import iconDownload from "../assets/icons/download.svg";
// import iconShare from '../assets/icons/share.svg'
import iconCog from "../assets/icons/settings.svg";
import iconTriangleUp from "../assets/icons/triangle-up.svg";
import iconTriangleDown from "../assets/icons/triangle-down.svg";

export default function Capturas() {
    const globalFilters = loadFilterSection("global") || {};
    const capturasFilters = loadFilterSection("capturas") || {};
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [permissions, setPermissions] = useState(null);
    
    // Datos iniciales del controlador
    const [especies, setEspecies] = useState([]);
    const [modalidades, setModalidades] = useState([]);
    const [provincias, setProvincias] = useState([]);
    const [cotos, setCotos] = useState([]);
    const initialTotalCapturas = {
        total: 0,
        ejemplares: 0,
        difference: 0,
        isIncrease: true,
    };
    const initialCapturasPorEspecie = [];
    const initialCapturasTemporales = {
        labels: [],
        data: [],
    };
    const initialTipoAutorizaciones = [];
    const initialCapturasTable = {
        data: [],
        current_page: 1,
        last_page: 1,
        total: 0,
        per_page: 10,
    };
    const initialEjemplaresPorValidacion = [];
    const initialCapturasSemanalPorEspecie = [];
    const initialTopCotos = [];
    const [initialTotalCotos, setInitialTotalCotos] = useState(0);

    // Cargar datos iniciales
    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const [especiesRes, modalidadesRes, provinciasRes, cotosRes, permissionsRes] = await Promise.all([
                api.get('/especies'),
                api.get('/modalidades'),
                api.get('/provincias'),
                api.get('/cotos'),
                api.get('/auth/permissions')
            ]);

            // Transformar especies de {value, label} a {id, nombre}
            const especiesTransformadas = (especiesRes.data || []).map(especie => ({
                id: especie.value,
                nombre: especie.label
            }));

            setEspecies(especiesTransformadas);
            setModalidades(modalidadesRes.data || []);
            setProvincias((provinciasRes.data || [])
                .filter((p) => {
                    const label = typeof p === 'string' ? p : (p.label ?? p.nombre ?? '');
                    return !label.toLowerCase().includes('demo');
                })
                .map((p) => {
                    if (typeof p === 'string') return p.toUpperCase();
                    const label = p.label ?? p.nombre ?? '';
                    return { ...p, label: label.toUpperCase() };
                }));
            setCotos(cotosRes.data || []);
            setTotalCotos(cotosRes.data?.length || 0);
            setPermissions(permissionsRes.data?.permissions ?? permissionsRes.data ?? null);
        } catch (err) {
            if (err.response?.status !== 401) {
                Sentry.captureException(new Error('Error loading initial data: ' + err.message));
            }
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Estados generales
    const [activeTab, setActiveTab] = useState("capturas");
    const [currentPage, setCurrentPage] = useState(
        initialCapturasTable.current_page,
    );
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedPeriod, setSelectedPeriod] = useState(
        capturasFilters.selectedPeriod || "mensual",
    );
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [chartType, setChartType] = useState("lineal");
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
    const [selectedProvincia, setSelectedProvincia] = useState(
        globalFilters.selectedProvincia || [],
    );
    const [selectedCoto, setSelectedCoto] = useState(
        globalFilters.selectedCoto || [],
    );
    //   const [isShareModalOpen, setIsShareModalOpen] = useState(false)
    const [isShareEmailModalOpen, setIsShareEmailModalOpen] = useState(false);
    const [isSpeciesModalOpen, setIsSpeciesModalOpen] = useState(false);
    const [fileType, setFileType] = useState("xls");
    const [shareType, setShareType] = useState("png");
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [isLoadingTable, setIsLoadingTable] = useState(false);
    const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
    const [isDownloadingZip, setIsDownloadingZip] = useState(false);

    // Estados para ordenamiento de la tabla
    const [sortBy, setSortBy] = useState("fecha");
    const [sortOrder, setSortOrder] = useState("desc");

    const [isAuthorizationTypeModalOpen, setIsAuthorizationTypeModalOpen] =
        useState(false);

    // Estados de datos
    const [totalCapturas, setTotalCapturas] = useState(initialTotalCapturas);
    const [capturasPorEspecie, setCapturasPorEspecie] = useState(
        initialCapturasPorEspecie,
    );
    const [capturasTemporales, setCapturasTemporales] = useState(
        initialCapturasTemporales,
    );
    const [tipoAutorizaciones, setTipoAutorizaciones] = useState(
        initialTipoAutorizaciones,
    );
    const [capturasTable, setCapturasTable] = useState(initialCapturasTable);
    const [ejemplaresPorValidacion, setEjemplaresPorValidacion] =
        useState(initialEjemplaresPorValidacion);
    const [capturasSemanalPorEspecie, setCapturasSemanalPorEspecie] = useState(
        initialCapturasSemanalPorEspecie,
    );
    const [topCotos, setTopCotos] = useState(initialTopCotos);
    const [totalCotos, setTotalCotos] = useState(initialTotalCotos);

    const chartSettingsRef = useRef(null);

    // Date range state
    const [dateRange, setDateRange] = useState(
        globalFilters.dateRange || getCurrentSeasonDateRange(),
    );

    // Lista de especies que se seleccionaban por defecto
    // const DEFAULT_SELECTED_SPECIES = [
    //     "Corzo",
    //     "Jabalí",
    //     "Cabra montés",
    //     "Ciervo",
    //     "Gamo",
    // ];

    // Inicializar especies visibles
    const [visibleSpecies, setVisibleSpecies] = useState(
        () => globalFilters.visibleSpecies || capturasFilters.visibleSpecies || {},
    );

    const speciesSettingsRef = useRef(null);
    const authorizationTypeSettingsRef = useRef(null);
    //   const chartSettingsRef = useRef(null)
    const shareEmailModalRef = useRef(null);
    const pageContentRef = useRef(null);

    const [visibleAuthorizationTypes, setVisibleAuthorizationTypes] = useState(
        capturasFilters.visibleAuthorizationTypes || {
            jabaliRececho: true,
            jabaliEmergencia: true,
            caceriaColectiva: true,
        },
    );

    useEffect(() => {
        saveFilterSection("global", {
            dateRange,
            selectedProvincia,
            selectedCoto,
            visibleSpecies,
        });
    }, [dateRange, selectedProvincia, selectedCoto, visibleSpecies]);

    useEffect(() => {
        saveFilterSection("capturas", {
            selectedPeriod,
            visibleAuthorizationTypes,
        });
    }, [selectedPeriod, visibleAuthorizationTypes]);

    // Función para obtener el icono correspondiente a cada animal
    const getAnimalIcon = (nombreEspecie) => {
        const iconMap = {
            Corzo: imgCorzo,
            Jabalí: imgJabali,
            "Cabra montés": imgCabraMontes,
            Ciervo: imgCiervo,
            "Paloma torcaz": imgPalomaTorcaz,
            "Muflón": imgMuflon,
            Rebeco: imgRebeco,
            Gamo: imgGamo,
            "Tórtola europea": imgTortola,
            Codorniz: imgCodorniz,
        };
        return iconMap[nombreEspecie] || imgCorzo; // Default icon
    };

    // Función para descargar en Excel
    const downloadExcel = async () => {
        try {
            const ExcelJS = await import("exceljs");
            const workbook = new ExcelJS.default.Workbook();

            // Hoja 1: Resumen General
            const wsResumen = workbook.addWorksheet("Resumen");
            wsResumen.addRow(["Panel de Datos - Capturas"]);
            wsResumen.addRow(["Fecha de generación:", new Date().toLocaleDateString("es-ES")]);
            wsResumen.addRow([
                "Rango de fechas:",
                `${formatDateForPicker(dateRange.startDate)} - ${formatDateForPicker(dateRange.endDate)}`,
            ]);
            wsResumen.addRow([]);
            wsResumen.addRow(["Métricas Principales"]);
            wsResumen.addRow(["Total Capturas", totalCapturas.total || 0]);
            wsResumen.addRow(["Diferencia mes anterior", totalCapturas.difference || 0]);
            wsResumen.addRow(["Tendencia", totalCapturas.isIncrease ? "Aumento" : "Disminución"]);

            // Hoja 2: Capturas por Especie y Sexo
            if (capturasPorEspecie.length > 0) {
                // Agrupar por especie
                const especiesAgrupadas = {};
                capturasPorEspecie.forEach((item) => {
                    const especieId = item.especie_id;
                    if (!especiesAgrupadas[especieId]) {
                        especiesAgrupadas[especieId] = {
                            nombre: item.nombre_especie,
                            macho: 0,
                            hembra: 0,
                            total: 0,
                        };
                    }
                    const capturas = parseInt(item.total_capturas) || 0;
                    if (item.sexo === 'm') {
                        especiesAgrupadas[especieId].macho += capturas;
                    } else if (item.sexo === 'f') {
                        especiesAgrupadas[especieId].hembra += capturas;
                    }
                    especiesAgrupadas[especieId].total += capturas;
                });

                const wsEspecie = workbook.addWorksheet("Por Especie y Sexo");
                wsEspecie.addRow(["Especie", "Macho", "Hembra", "Total"]);
                Object.values(especiesAgrupadas).forEach((item) => {
                    wsEspecie.addRow([
                        item.nombre || "N/A",
                        item.macho,
                        item.hembra,
                        item.total,
                    ]);
                });
            }

            // Hoja 3: Capturas Totales
            if (capturasTemporales.labels && capturasTemporales.labels.length > 0) {
                const wsTemporales = workbook.addWorksheet("Capturas Totales");
                wsTemporales.addRow(["Período", "Capturas"]);
                capturasTemporales.labels.forEach((label, index) => {
                    wsTemporales.addRow([label, capturasTemporales.data[index] || 0]);
                });
            }

            // Hoja 4: Capturas por modalidad
            if (tipoAutorizaciones.length > 0) {
                const wsTipo = workbook.addWorksheet("Capturas por modalidad");
                wsTipo.addRow(["Tipo", "Total", "Porcentaje (%)"]);
                tipoAutorizaciones.forEach((item) => {
                    wsTipo.addRow([
                        item.tipo,
                        item.total || 0,
                        item.porcentaje || 0,
                    ]);
                });
            }

            // Hoja 5: Tabla de Capturas - Obtener todos los registros
            try {
                const selectedSpeciesIds = Object.keys(visibleSpecies)
                    .filter((id) => visibleSpecies[id])
                    .join(",");

                const params = new URLSearchParams({
                    search: searchTerm,
                    per_page: "999999",
                    sort_by: sortBy,
                    sort_order: sortOrder,
                    start_date: dateRange.startDate,
                    end_date: dateRange.endDate,
                    especies: selectedSpeciesIds,
                });

                const response = await api.get("/capturas/data/table?" + params);
                const data = response.data;
                if (true) {
                    if (data.data && data.data.length > 0) {
                        const wsTabla = workbook.addWorksheet("Tabla Capturas");
                        wsTabla.addRow([
                            "Especie",
                            "Precinto",
                            "Ejemplares",
                            "Modalidad",
                            "Cazador",
                            "Coto",
                            "Provincia",
                            "Tipo",
                            "Fecha",
                        ]);
                        data.data.forEach((item) => {
                            wsTabla.addRow([
                                item.especie,
                                item.precinto,
                                item.ejemplares,
                                item.modalidad,
                                item.cazador,
                                item.coto,
                                item.provincia,
                                item.tipo === "automatica" ? "APP" : "PAPEL",
                                item?.fecha,
                            ]);
                        });
                    }
                }
            } catch (error) {
                if (error.response?.status !== 401) {
                    Sentry.captureException(new Error("Error cargando datos de capturas: " + error.message));
                }
            }

            // Hoja 6: Capturas Semanales por Especie
            if (capturasSemanalPorEspecie.length > 0) {
                // Función para obtener las fechas de una semana ISO
                const getWeekDateRange = (year, week) => {
                    // Obtener el primer día del año
                    const firstDayOfYear = new Date(year, 0, 1);
                    // Calcular el lunes de la semana 1
                    const daysToMonday =
                        (firstDayOfYear.getDay() === 0 ? -6 : 1) -
                        firstDayOfYear.getDay();
                    const firstMonday = new Date(firstDayOfYear);
                    firstMonday.setDate(firstDayOfYear.getDate() + daysToMonday);

                    // Calcular el lunes de la semana especificada
                    const monday = new Date(firstMonday);
                    monday.setDate(firstMonday.getDate() + (week - 1) * 7);

                    // Calcular el domingo
                    const sunday = new Date(monday);
                    sunday.setDate(monday.getDate() + 6);

                    // Formatear fechas
                    const formatDate = (date) => {
                        const day = String(date.getDate()).padStart(2, "0");
                        const month = String(date.getMonth() + 1).padStart(2, "0");
                        const year = date.getFullYear();
                        return `${day}/${month}/${year}`;
                    };

                    return `${formatDate(monday)} - ${formatDate(sunday)}`;
                };

                const especies = [
                    ...new Set(
                        capturasSemanalPorEspecie.map(
                            (item) => item.nombre_especie,
                        ),
                    ),
                ];
                const semanas = [
                    ...new Set(
                        capturasSemanalPorEspecie.map(
                            (item) => `${item.anio}-S${item.semana}`,
                        ),
                    ),
                ].sort();

                const wsSemanal = workbook.addWorksheet("Capturas Semanales");
                wsSemanal.addRow(["Semana", ...especies]);
                
                semanas.forEach((semana) => {
                    const [anio, sem] = semana.split("-S");
                    const weekRange = getWeekDateRange(
                        parseInt(anio),
                        parseInt(sem),
                    );
                    const row = [weekRange];

                    especies.forEach((especie) => {
                        // Suma todas las validaciones de esa semana (APP y
                        // cualquier variante de papel, incluida "null"), no solo
                        // automatica/manual, para no perder capturas del total.
                        const total = capturasSemanalPorEspecie
                            .filter(
                                (item) =>
                                    item.nombre_especie === especie &&
                                    item.anio == anio &&
                                    item.semana == sem,
                            )
                            .reduce(
                                (sum, item) => sum + (parseInt(item.total) || 0),
                                0,
                            );
                        row.push(total);
                    });

                    wsSemanal.addRow(row);
                });
            }

            // Hoja 7: Ejemplares por Validación
            if (ejemplaresPorValidacion.length > 0) {
                const wsValidacion = workbook.addWorksheet("Ejemplares por Validación");
                wsValidacion.addRow([
                    "Validación",
                    "Total Capturas",
                    "Total Ejemplares",
                ]);
                // Cualquier validación que no sea "automatica" (APP) es papel,
                // así que se agrupan bajo una sola fila "PAPEL" en vez de una
                // fila por cada valor crudo (manual, null, etc.).
                const validacionTotales = {};
                ejemplaresPorValidacion.forEach((item) => {
                    const label = item.validacion === "automatica" ? "APP" : "PAPEL";
                    if (!validacionTotales[label]) {
                        validacionTotales[label] = { total_capturas: 0, total_ejemplares: 0 };
                    }
                    validacionTotales[label].total_capturas += parseInt(item.total_capturas) || 0;
                    validacionTotales[label].total_ejemplares += parseInt(item.total_ejemplares) || 0;
                });
                Object.entries(validacionTotales).forEach(([label, totales]) => {
                    wsValidacion.addRow([
                        label,
                        totales.total_capturas,
                        totales.total_ejemplares,
                    ]);
                });
            }

            // Hoja 8: Top Cotos
            if (topCotos.length > 0) {
                const wsCotos = workbook.addWorksheet("Top Cotos");
                wsCotos.addRow(["Coto", "Total Capturas"]);
                topCotos.forEach((item) => {
                    wsCotos.addRow([
                        item.nombre_coto || item.coto_id,
                        item.total_capturas || 0,
                    ]);
                });
            }

            const fileName = `capturas_${new Date().toISOString().split("T")[0]}.xlsx`;
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
        } catch (error) {
            if (error.response?.status !== 401) {
                Sentry.captureException(new Error("Error exportando Excel: " + error.message));
            }
            alert("Error al generar el archivo Excel. Por favor, intente de nuevo.");
            console.error("Error en downloadExcel:", error);
        }
    };

    // Función para convertir imagen a base64
    const imageToBase64 = (img) => {
        return new Promise((resolve) => {
            if (!img.src || img.src.startsWith("data:")) {
                resolve(null);
                return;
            }

            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            const image = new Image();
            image.crossOrigin = "anonymous";

            image.onload = () => {
                canvas.width = image.naturalWidth || image.width;
                canvas.height = image.naturalHeight || image.height;
                ctx.drawImage(image, 0, 0);
                try {
                    const base64 = canvas.toDataURL("image/png");
                    resolve(base64);
                } catch (e) {
                    resolve(null);
                }
            };

            image.onerror = () => {
                resolve(null);
            };

            const separator = img.src.includes("?") ? "&" : "?";
            image.src = img.src + separator + "t=" + Date.now();
        });
    };

    // Función para descargar en PDF
    const downloadPDF = async () => {
        if (!pageContentRef.current) return;

        setIsDownloadModalOpen(false);
        await new Promise((resolve) => setTimeout(resolve, 300));

        try {
            // Imports dinámicos para mejor rendimiento
            const [html2canvasModule, jsPDFModule] = await Promise.all([
                import("html2canvas"),
                import("jspdf"),
            ]);
            const html2canvas = html2canvasModule.default;
            const jsPDF = jsPDFModule.default;

            const loadingDiv = document.createElement("div");
            loadingDiv.id = "pdf-loading";
            loadingDiv.style.cssText =
                "position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;";
            loadingDiv.innerHTML =
                '<div style="background: white; padding: 20px 40px; border-radius: 8px; font-size: 16px;">Generando PDF...</div>';
            document.body.appendChild(loadingDiv);

            const images = pageContentRef.current.querySelectorAll("img");
            const originalSrcs = new Map();

            for (const img of images) {
                if (img.src && !img.src.startsWith("data:")) {
                    originalSrcs.set(img, img.src);
                    const base64 = await imageToBase64(img);
                    if (base64) {
                        img.src = base64;
                    }
                }
            }

            await new Promise((resolve) => setTimeout(resolve, 100));

            const canvas = await html2canvas(pageContentRef.current, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                logging: false,
                backgroundColor: "#f5f5f5",
                imageTimeout: 0,
            });

            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF({
                orientation:
                    canvas.width > canvas.height ? "landscape" : "portrait",
                unit: "mm",
                format: "a4",
            });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
            const imgX = (pdfWidth - imgWidth * ratio) / 2;

            // * Quitamos el titulo por ahora *
            // pdf.setFontSize(16);
            // pdf.text("Panel de Datos - Capturas", pdfWidth / 2, 10, {
            //     align: "center",
            // });

            const scaledHeight = imgHeight * ratio;
            const pageContentHeight = pdfHeight - 20;

            if (scaledHeight <= pageContentHeight) {
                pdf.addImage(
                    imgData,
                    "PNG",
                    imgX,
                    15,
                    imgWidth * ratio,
                    imgHeight * ratio,
                );
            } else {
                let remainingHeight = imgHeight;
                let sourceY = 0;
                let isFirstPage = true;

                while (remainingHeight > 0) {
                    if (!isFirstPage) {
                        pdf.addPage();
                    }

                    const pageHeight = pageContentHeight / ratio;
                    const sliceHeight = Math.min(pageHeight, remainingHeight);

                    const tempCanvas = document.createElement("canvas");
                    tempCanvas.width = imgWidth;
                    tempCanvas.height = sliceHeight;
                    const tempCtx = tempCanvas.getContext("2d");
                    tempCtx.drawImage(
                        canvas,
                        0,
                        sourceY,
                        imgWidth,
                        sliceHeight,
                        0,
                        0,
                        imgWidth,
                        sliceHeight,
                    );

                    const tempImgData = tempCanvas.toDataURL("image/png");
                    pdf.addImage(
                        tempImgData,
                        "PNG",
                        imgX,
                        isFirstPage ? 15 : 10,
                        imgWidth * ratio,
                        sliceHeight * ratio,
                    );

                    sourceY += sliceHeight;
                    remainingHeight -= sliceHeight;
                    isFirstPage = false;
                }
            }

            pdf.setFontSize(10);
            pdf.text(
                `Generado el ${new Date().toLocaleDateString("es-ES")} - Rango: ${formatDateForPicker(dateRange.startDate)} a ${formatDateForPicker(dateRange.endDate)}`,
                pdfWidth / 2,
                pdfHeight - 5,
                { align: "center" },
            );

            const fileName = `capturas_${new Date().toISOString().split("T")[0]}.pdf`;
            pdf.save(fileName);

            for (const [img, originalSrc] of originalSrcs) {
                img.src = originalSrc;
            }

            document.body.removeChild(loadingDiv);
        } catch (error) {
            if (error.response?.status !== 401) {
                Sentry.captureException(new Error("Error generating PDF: " + error.message));
            }
            const loadingDiv = document.getElementById("pdf-loading");
            if (loadingDiv) {
                document.body.removeChild(loadingDiv);
            }
            alert("Error al generar el PDF. Por favor, intente de nuevo.");
        }
    };

    // Función para manejar la descarga según el tipo seleccionado
    const handleDownload = () => {
        if (fileType === "xls") {
            downloadExcel();
            setIsDownloadModalOpen(false);
        } else if (fileType === "pdf") {
            downloadPDF();
        }
    };

    // Función para descargar solo los datos de la tabla en Excel
    const downloadTableExcel = async () => {
        setIsDownloadingExcel(true);
        try {
            // Obtener TODOS los datos del API sin paginación
            const selectedSpeciesIds = Object.keys(visibleSpecies)
                .filter((id) => visibleSpecies[id])
                .join(",");

            const params = new URLSearchParams({
                search: searchTerm,
                per_page: "999999",
                sort_by: sortBy,
                sort_order: sortOrder,
                start_date: dateRange.startDate,
                end_date: dateRange.endDate,
                especies: selectedSpeciesIds,
            });

            // Añadir filtros de provincia y coto
            if (Array.isArray(selectedProvincia) && selectedProvincia.length > 0) {
                selectedProvincia.forEach((p) => params.append("provincia[]", p));
            }
            if (Array.isArray(selectedCoto) && selectedCoto.length > 0) {
                selectedCoto.forEach((c) => params.append("coto_id[]", c));
            }

            const response = await api.get("/capturas/data/table?" + params);
            const allData = response.data.data || [];

            if (allData.length === 0) {
                alert("No hay datos para descargar");
                return;
            }

            const ExcelJS = await import("exceljs");
            const workbook = new ExcelJS.default.Workbook();
            
            // Crear hoja de trabajo
            const worksheet = workbook.addWorksheet("Capturas");
            
            // Agregar encabezados
            const isAdmin = permissions?.canViewVisor === true;
            const exportColumns = isAdmin
                ? [...columns, { key: 'lat', label: 'X' }, { key: 'lng', label: 'Y' }]
                : columns;
            const headers = exportColumns.map(col => col.label);
            const headerRow = worksheet.addRow(headers);

            // Estilo para encabezados
            headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            headerRow.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF2D6A4F' }
            };
            headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

            // Agregar datos
            allData.forEach(row => {
                const rowData = exportColumns.map(col => {
                    const value = row[col.key];
                    if (col.key === 'sexo') {
                        if (value === 'M') return 'Macho';
                        if (value === 'F') return 'Hembra';
                        if (value === 'I') return 'Indeterminado';
                        return value || '';
                    } else if (col.key === 'tipo') {
                        return value || '';
                    } else if (col.key === 'fecha') {
                        return row.fecha_hora || value || '';
                    } else if ((col.key === 'lat' || col.key === 'lng') && row.lat && row.lng) {
                        const [easting, northing] = proj4('EPSG:4326', 'EPSG:25830', [parseFloat(row.lng), parseFloat(row.lat)]);
                        return col.key === 'lat' ? Math.round(northing) : Math.round(easting);
                    }
                    return value ?? '';
                });
                worksheet.addRow(rowData);
            });
            
            // Ajustar ancho de columnas
            worksheet.columns.forEach((column, index) => {
                let maxLength = headers[index].length;
                column.eachCell({ includeEmpty: false }, cell => {
                    const cellLength = cell.value ? cell.value.toString().length : 0;
                    if (cellLength > maxLength) {
                        maxLength = cellLength;
                    }
                });
                column.width = Math.min(maxLength + 2, 50);
            });
            
            // Generar archivo
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `capturas_tabla_${new Date().toISOString().split('T')[0]}.xlsx`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error al descargar Excel:", error);
            if (error.response?.status !== 401) {
                Sentry.captureException(error);
            }
            alert("Hubo un error al descargar el archivo Excel");
        } finally {
            setIsDownloadingExcel(false);
        }
    };

    // Descargar ZIP con imágenes de capturas (solo admin)
    const downloadImagesZip = async () => {
        setIsDownloadingZip(true);
        try {
            const selectedSpeciesIds = Object.keys(visibleSpecies)
                .filter((id) => visibleSpecies[id])
                .join(",");

            const params = new URLSearchParams({
                search: searchTerm,
                sort_by: sortBy,
                sort_order: sortOrder,
                start_date: dateRange.startDate,
                end_date: dateRange.endDate,
                especies: selectedSpeciesIds,
            });

            if (Array.isArray(selectedProvincia) && selectedProvincia.length > 0) {
                selectedProvincia.forEach((p) => params.append("provincia[]", p));
            }
            if (Array.isArray(selectedCoto) && selectedCoto.length > 0) {
                selectedCoto.forEach((c) => params.append("coto_id[]", c));
            }

            const response = await api.get("/capturas/export/images-zip?" + params, {
                responseType: "blob",
            });

            const blob = new Blob([response.data], { type: "application/zip" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `capturas_imagenes_${new Date().toISOString().split("T")[0]}.zip`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error al descargar imágenes:", error);
            if (error.response?.status === 404) {
                alert("No hay capturas con imágenes para los filtros seleccionados.");
            } else if (error.response?.status !== 401) {
                Sentry.captureException(error);
                alert("Hubo un error al descargar las imágenes.");
            }
        } finally {
            setIsDownloadingZip(false);
        }
    };

    const handleRowClick = (row) => {
        navigate(`/capturas/${row.id}`);
    };

    const tabs = useMemo(() => {
        const allTabs = [
            { id: "general", label: "General" },
            { id: "usuarios", label: "Usuarios" },
            { id: "autorizaciones", label: "Autorizaciones" },
            { id: "cupos", label: "Cupos" },
            { id: "capturas", label: "Capturas" },
        ];
        // Temporalmente deshabilitado - mostrar siempre el tab de usuarios
        // Descomentar cuando el API devuelva correctamente canViewUsers
        
        if (!permissions?.canViewUsers) {
            return allTabs.filter((tab) => tab.id !== "usuarios");
        }
        
        return allTabs;
    }, [permissions?.canViewUsers]);

    const handleTabChange = useCallback((tab) => {
        setActiveTab(tab);
        const routes = {
            general: "/general",
            usuarios: "/usuarios",
            autorizaciones: "/autorizaciones",
            cupos: "/cupos",
            capturas: "/capturas",
        };
        if (routes[tab]) navigate(routes[tab]);
    }, [navigate]);

    const periodOptions = useMemo(
        () => [
            { value: "mensual", label: "Mensual" },
            { value: "anual", label: "Anual" },
            { value: "semanal", label: "Semanal" },
        ],
        [],
    );

    // Función para convertir fecha ISO a formato DD/MM/YYYY
    const formatDateForPicker = (dateString) => {
        if (!dateString) return "";
        const [year, month, day] = dateString.split("-");
        return `${day}/${month}/${year}`;
    };

    // Cargar datos de la tabla con paginación, búsqueda y ordenamiento
    const loadTableData = async (
        page = 1,
        search = "",
        sortColumn = sortBy,
        sortDir = sortOrder,
    ) => {
        setIsLoadingTable(true);
        try {
            // Obtener IDs de especies visibles
            const selectedSpeciesIds = Object.keys(visibleSpecies)
                .filter((id) => visibleSpecies[id])
                .join(",");

            const params = new URLSearchParams({
                page: page.toString(),
                search: search,
                per_page: "10",
                sort_by: sortColumn,
                sort_order: sortDir,
                start_date: dateRange.startDate,
                end_date: dateRange.endDate,
                especies: selectedSpeciesIds,
            });

            // Añadir filtros de provincia y coto
            if (Array.isArray(selectedProvincia) && selectedProvincia.length > 0) {
                selectedProvincia.forEach((p) => params.append("provincia[]", p));
            }
            if (Array.isArray(selectedCoto) && selectedCoto.length > 0) {
                selectedCoto.forEach((c) => params.append("coto_id[]", c));
            }

            const response = await api.get("/capturas/data/table?" + params);
            const data = response.data;
            setCapturasTable(data);
            setCurrentPage(data.current_page);
        } catch (error) {
            if (error.response?.status !== 401) {
                Sentry.captureException(new Error("Error loading table data: " + error.message));
            }
        } finally {
            setIsLoadingTable(false);
        }
    };

    // Cargar datos de gráficos
    const loadData = async () => {
        if (!dateRange.startDate || !dateRange.endDate) return;

        setIsLoadingData(true);
        try {
            // Obtener IDs de especies visibles
            const selectedSpeciesIds = Object.keys(visibleSpecies)
                .filter((id) => visibleSpecies[id])
                .join(",");

            const params = new URLSearchParams({
                start_date: dateRange.startDate,
                end_date: dateRange.endDate,
                periodo: selectedPeriod,
                especies: selectedSpeciesIds,
            });

            // Añadir filtros de provincia y coto
            if (Array.isArray(selectedProvincia) && selectedProvincia.length > 0) {
                selectedProvincia.forEach((p) => params.append("provincia[]", p));
            }
            if (Array.isArray(selectedCoto) && selectedCoto.length > 0) {
                selectedCoto.forEach((c) => params.append("coto_id[]", c));
            }

            // Cargar total de capturas
            const totalResponse = await api.get(`/capturas/data/total?${params}`);
            const totalData = totalResponse.data;
            setTotalCapturas(totalData);

            // Cargar datos de capturas por especie
            const especieResponse = await api.get(
                `/capturas/data/por-especie?${params}`,
            );
            const especieData = especieResponse.data;
            setCapturasPorEspecie(especieData);

            // Cargar capturas temporales
            const temporalesResponse = await api.get(
                `/capturas/data/temporales?${params}`,
            );
            const temporalesData = temporalesResponse.data;
            setCapturasTemporales(temporalesData);

            // Cargar tipo autorizaciones
            const tipoResponse = await api.get(
                `/capturas/data/tipo-autorizaciones?${params}`,
            );
            const tipoData = tipoResponse.data;
            setTipoAutorizaciones(tipoData);

            // Cargar ejemplares por validación
            const ejemplaresSexoValidacionResponse = await api.get(
                `/capturas/data/ejemplares-por-validacion?${params}`,
            );
            const ejemplaresSexoValidacionData =
                ejemplaresSexoValidacionResponse.data;
            setEjemplaresPorValidacion(ejemplaresSexoValidacionData);

            // Cargar capturas semanal por especie
            const semanalEspecieResponse = await api.get(
                `/capturas/data/semanal-por-especie?${params}`,
            );
            const semanalEspecieData = semanalEspecieResponse.data;
            setCapturasSemanalPorEspecie(semanalEspecieData);

            // Cargar top cotos
            const topCotosResponse = await api.get(
                `/capturas/data/top-cotos?${params}`,
            );
            const topCotosData = topCotosResponse.data;
            setTopCotos(topCotosData);
        } catch (error) {
            if (error.response?.status !== 401) {
                Sentry.captureException(new Error("Error loading data: " + error.message));
            }
        } finally {
            setIsLoadingData(false);
        }
    };

    // Handler para cambio de página
    const handlePageChange = useCallback((page) => {
        setCurrentPage(page);
    }, []);

    // Handler para ordenamiento
    const handleSort = useCallback(
        (columnKey) => {
            setSortBy((prev) => {
                const newSortOrder =
                    prev === columnKey && sortOrder === "asc" ? "desc" : "asc";
                setSortOrder(newSortOrder);
                // Para el campo "tipo", ordena por "validacion" que es el campo real en la BD
                const sortColumn = columnKey === "tipo" ? "validacion" : columnKey;
                loadTableData(1, searchTerm, sortColumn, newSortOrder);
                return columnKey;
            });
        },
        [sortOrder, searchTerm],
    );

    // Efecto consolidado para cargar datos de la tabla con debounce
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (dateRange.startDate && dateRange.endDate) {
                const pageToLoad = searchTerm ? 1 : currentPage;
                loadTableData(pageToLoad, searchTerm, sortBy, sortOrder);
            }
        }, searchTerm ? 500 : 0);

        return () => clearTimeout(timeoutId);
    }, [currentPage, dateRange.startDate, dateRange.endDate, visibleSpecies, selectedProvincia, selectedCoto, sortBy, sortOrder, searchTerm]);

    // Efecto para inicializar especies visibles cuando se cargan las especies
    useEffect(() => {
        if (!especies || especies.length === 0) return;

        const hasAllKeys = especies.every((especie) =>
            Object.prototype.hasOwnProperty.call(visibleSpecies, especie.id),
        );

        if (Object.keys(visibleSpecies).length === 0 || !hasAllKeys) {
            const initial = {};
            especies.forEach((especie) => {
                initial[especie.id] = true;
            });
            setVisibleSpecies(initial);
        }
    }, [especies, visibleSpecies]);

    // Efecto para cargar datos cuando cambian los filtros
    useEffect(() => {
        if (dateRange.startDate && dateRange.endDate && Object.keys(visibleSpecies).length > 0) {
            loadData();
        }
    }, [
        dateRange.startDate,
        dateRange.endDate,
        selectedPeriod,
        visibleSpecies,
        selectedProvincia,
        selectedCoto,
    ]);

    // Configuración del gráfico de capturas temporales (Line/Bar)
    const capturasTemporalesChartData = useMemo(
        () => ({
            labels: capturasTemporales.labels || [],
            datasets: [
                {
                    label: "Capturas",
                    data: capturasTemporales.data || [],
                    borderColor: "#2D6A4F",
                    backgroundColor:
                        chartType === "lineal"
                            ? "rgba(45, 106, 79, 0.1)"
                            : "#2D6A4F",
                    fill: chartType === "lineal",
                    tension: 0.4,
                },
            ],
        }),
        [capturasTemporales, chartType],
    );

    const lineChartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false,
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                },
            },
        }),
        [],
    );

    // Configuración del gráfico de capturas por especie (Bar horizontal)
    // Ahora incluye desglose por sexo
    const capturasPorEspecieChartData = useMemo(() => {
        if (!capturasPorEspecie || capturasPorEspecie.length === 0) {
            return { labels: [], datasets: [] };
        }

        // Agrupar por especie
        const groupedByEspecie = {};
        capturasPorEspecie.forEach((item) => {
            if (!groupedByEspecie[item.nombre_especie]) {
                groupedByEspecie[item.nombre_especie] = {
                    macho: 0,
                    hembra: 0,
                    ambos: 0,
                    total: 0
                };
            }
            const sexo = item.sexo === 'm' ? 'macho' : item.sexo === 'f' ? 'hembra' : 'ambos';
            groupedByEspecie[item.nombre_especie][sexo] += parseInt(item.total_ejemplares) || 0;
            groupedByEspecie[item.nombre_especie].total += parseInt(item.total_ejemplares) || 0;
        });

        const especies = Object.keys(groupedByEspecie).sort((a, b) => a.localeCompare(b));

        return {
            labels: especies,
            datasets: [
                {
                    label: "Macho",
                    data: especies.map((e) => groupedByEspecie[e].macho),
                    backgroundColor: "#2D6A4F",
                    borderWidth: 0,
                },
                {
                    label: "Hembra",
                    data: especies.map((e) => groupedByEspecie[e].hembra),
                    backgroundColor: "#74C69D",
                    borderWidth: 0,
                },
                {
                    label: "Ambos sexos",
                    data: especies.map((e) => groupedByEspecie[e].ambos),
                    backgroundColor: "#95D5B2",
                    borderWidth: 0,
                },
            ],
        };
    }, [capturasPorEspecie]);

    const barChartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: "y",
            plugins: {
                legend: {
                    display: true,
                    position: "bottom",
                },
            },
            scales: {
                x: {
                    beginAtZero: true,
                    stacked: true,
                },
                y: {
                    stacked: true,
                },
            },
        }),
        [],
    );

    // Configuración del gráfico de tipo de autorizaciones (Doughnut)
    const tipoAutorizacionesChartData = useMemo(
        () => {
            if (
                !tipoAutorizaciones ||
                tipoAutorizaciones.length === 0
            ) {
                return { labels: [], datasets: [] };
            }

            return {
                labels: tipoAutorizaciones
                    .map((item) => item.tipo)
                    .filter((label) => label !== undefined && label !== null),
                datasets: [
                    {
                        data: tipoAutorizaciones
                            .map((item) => item.total || 0)
                            .filter((_, index) => 
                                tipoAutorizaciones[index].tipo !== undefined &&
                                tipoAutorizaciones[index].tipo !== null
                            ),
                        backgroundColor: [
                            "#2D6A4F",
                            "#40916C",
                            "#52B788",
                            "#74C69D",
                        ],
                        borderWidth: 0,
                    },
                ],
            };
        },
        [tipoAutorizaciones],
    );

    const pieChartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: true,
                    position: "bottom",
                },
                tooltip: {
                    callbacks: {
                        label: function (context) {
                            const label = context.label || 'Sin especificar';
                            const value = context.raw || 0;
                            return `${label}: ${formatNumber(value)}`;
                        },
                    },
                },
            },
        }),
        [tipoAutorizaciones],
    );

    // Configuración del gráfico de ejemplares por validación
    // Ahora solo agrupa por tipo de validación (sin especie ni sexo)
    const ejemplaresPorValidacionChartData = useMemo(() => {
        if (
            !ejemplaresPorValidacion ||
            ejemplaresPorValidacion.length === 0
        ) {
            return { labels: [], datasets: [] };
        }

        const validacionData = {};

        ejemplaresPorValidacion.forEach((item) => {
            // Cualquier valor de validación que no sea "automatica" (APP) se
            // agrupa bajo "PAPEL", así que se agrega usando ya la etiqueta
            // final para no generar varias entradas duplicadas de "PAPEL".
            const label = item.validacion === 'automatica' ? 'APP' : 'PAPEL';

            if (!validacionData[label]) {
                validacionData[label] = 0;
            }

            validacionData[label] += parseInt(item.total_ejemplares) || 0;
        });

        const labels = Object.keys(validacionData);
        const data = Object.values(validacionData);

        return {
            labels: labels.length > 0 ? labels : ['Sin datos'],
            datasets: [
                {
                    label: "Ejemplares",
                    data: data.length > 0 ? data : [0],
                    backgroundColor: [
                        "#2D6A4F",
                        "#40916C",
                        "#52B788",
                        "#74C69D",
                    ],
                    borderWidth: 0,
                },
            ],
        };
    }, [ejemplaresPorValidacion]);

    // Configuración del gráfico de capturas semanal por especie
    const capturasSemanalPorEspecieChartData = useMemo(() => {
        // Función para obtener las fechas de una semana ISO
        const getWeekDateRange = (year, week) => {
            const firstDayOfYear = new Date(year, 0, 1);
            const daysToMonday =
                (firstDayOfYear.getDay() === 0 ? -6 : 1) -
                firstDayOfYear.getDay();
            const firstMonday = new Date(firstDayOfYear);
            firstMonday.setDate(firstDayOfYear.getDate() + daysToMonday);

            const monday = new Date(firstMonday);
            monday.setDate(firstMonday.getDate() + (week - 1) * 7);

            const sunday = new Date(monday);
            sunday.setDate(monday.getDate() + 6);

            const formatDate = (date) => {
                const day = String(date.getDate()).padStart(2, "0");
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const year = date.getFullYear();
                return `${day}/${month}/${year}`;
            };

            return `${formatDate(monday)} - ${formatDate(sunday)}`;
        };

        const especies = [
            ...new Set(
                capturasSemanalPorEspecie.map((item) => item.nombre_especie),
            ),
        ].sort((a, b) => a.localeCompare(b));
        const semanas = [
            ...new Set(
                capturasSemanalPorEspecie.map(
                    (item) => `${item.anio}-S${item.semana}`,
                ),
            ),
        ].sort();

        // Convertir las semanas a rangos de fechas para las etiquetas
        const weekLabels = semanas.map((semana) => {
            const [anio, sem] = semana.split("-S");
            return getWeekDateRange(parseInt(anio), parseInt(sem));
        });
        // console.log(weekLabels);
        const datasets = especies.map((especie, index) => {
            const colors = [
                "#2D6A4F",
                "#40916C",
                "#52B788",
                "#74C69D",
                "#95D5B2",
                "#B7E4C7",
            ];
            const especieData = capturasSemanalPorEspecie.filter(
                (item) => item.nombre_especie === especie,
            );

            const dataPoints = semanas.map((semana) => {
                const [anio, sem] = semana.split("-S");
                // Suma todas las validaciones de esa semana (APP y cualquier
                // variante de papel, incluida "null"), no solo automatica/manual,
                // para no perder capturas del total semanal.
                return especieData
                    .filter(
                        (item) => item.anio == anio && item.semana == sem,
                    )
                    .reduce((sum, item) => sum + (parseInt(item.total) || 0), 0);
            });

            return {
                label: especie,
                data: dataPoints,
                borderColor: colors[index % colors.length],
                backgroundColor: colors[index % colors.length] + "20",
                fill: false,
                tension: 0.4,
            };
        });

        return {
            labels: weekLabels,
            datasets: datasets,
        };
    }, [capturasSemanalPorEspecie]);

    const groupedBarChartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: true,
                    position: "bottom",
                },
            },
            scales: {
                x: {
                    beginAtZero: true,
                },
                y: {
                    beginAtZero: true,
                },
            },
        }),
        [],
    );

    // Opciones específicas para el gráfico de capturas semanales
    const capturasSemanalChartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: true,
                    position: "bottom",
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                },
            },
        }),
        [],
    );

    // Configuración del gráfico de top cotos
    const topCotosChartData = {
        labels: topCotos.map(
            (item) => item.nombre_coto + "(" + item.matricula_coto + ")",
        ),
        datasets: [
            {
                label: "Capturas",
                data: topCotos.map((item) => item.total_capturas),
                backgroundColor: "#2D6A4F",
                borderColor: "#2D6A4F",
                borderWidth: 1,
            },
        ],
    };

    const horizontalBarOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: "y",
            plugins: {
                legend: {
                    display: false,
                },
            },
            scales: {
                x: {
                    beginAtZero: true,
                },
            },
        }),
        [],
    );

    // Columnas de la tabla
    const columns = [
        {
            key: "especie",
            label: "Especie",
            sortable: true,
            render: (value) => {
                return (
                    <span
                        style={{
                            color: "var(--dk-green-700)",
                            fontWeight: 600,
                        }}
                    >
                        {value}
                    </span>
                );
            },
        },
        {
            key: "sexo",
            label: "Sexo",
            sortable: true,
            render: (value) => {
                return (
                    <span>
                        {value === "m"
                            ? "Macho"
                            : value === "f"
                            ? "Hembra"
                            : "No especificado"}
                    </span>
                );
            },
        },
        {
            key: "precinto",
            label: "Precinto",
            sortable: true,
        },
        {
            key: "ejemplares",
            label: "Ejemplares",
            sortable: true,
        },
        {
            key: "modalidad",
            label: "Modalidad",
            sortable: true,
        },
        {
            key: "cazador",
            label: "Cazador",
            sortable: true,
        },
        {
            key: "coto",
            label: "Coto",
            sortable: true,
        },
        {
            key: "provincia",
            label: "Provincia",
            sortable: true,
        },
        {
            key: "tipo",
            label: "Tipo",
            sortable: true,
            render: (value) => {
                const displayValue = value === "automatica" ? "CapturCYL" : "Papel";
                return <Tag variant="info">{displayValue}</Tag>;
            },
        },
        {
            key: "fecha",
            label: "Fecha",
            sortable: true,
        },
    ];

    return (
        <>
            {loading && <LoadingScreen />}
            {error && <div className="error-message">Error: {error}</div>}
            {!loading && !error && (
        <div className="page-capturas" ref={pageContentRef}>
            <h1 className="page-title">Panel de datos del Sistema de control telemático de capturas en los cotos de caza de Castilla y León</h1>

            <div className="page-tabs">
                <TabBar
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                />
            </div>

            <div className="page-content">
                <div className="page-filters">
                    <div style={{display: 'flex', gap: '35px', flexWrap: 'wrap'}}>
                        <div className="filter-group">
                            <label className="filter-label">
                                Seleccione un rango de fechas
                            </label>
                            <DateRangePicker
                                startDate={formatDateForPicker(dateRange.startDate)}
                                endDate={formatDateForPicker(dateRange.endDate)}
                                onChange={setDateRange}
                            />
                        </div>
                        <div className="filter-group">
                            <label className="filter-label">
                                Provincia
                            </label>
                            <Dropdown
                                options={provincias}
                                value={selectedProvincia}
                                onChange={(value) => {
                                    setSelectedProvincia(value);
                                    setCurrentPage(1);
                                }}
                                placeholder="Todas las provincias"
                                searchable={true}
                                multiple={true}
                            />
                        </div>
                        <div className="filter-group">
                            <label className="filter-label">
                                Coto
                            </label>
                            <Dropdown
                                options={cotos}
                                value={selectedCoto}
                                onChange={(value) => {
                                    setSelectedCoto(value);
                                    setCurrentPage(1);
                                }}
                                placeholder="Todos los cotos"
                                searchable={true}
                                multiple={true}
                            />
                        </div>
                        <div className="filter-group">
                            <label className="filter-label">
                                Especies
                            </label>
                            <Button
                                variant="primary"
                                size="s"
                                style={{ position: 'relative' }}
                                onClick={() =>
                                    setIsSpeciesModalOpen(true)
                                }
                            >
                                {/* <img src={iconCog} alt="" /> */}
                                Selecciona las especies
                                {Object.values(visibleSpecies).filter(Boolean).length > 0 && (
                                    <span style={{ position: 'absolute', top: '-8px', right: '-8px', backgroundColor: '#2D6A4F', color: 'white', borderRadius: '50%', width: '22px', height: '22px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', lineHeight: '1', pointerEvents: 'none' }}>
                                        {Object.values(visibleSpecies).filter(Boolean).length}
                                    </span>
                                )}
                            </Button>
                        </div>
                    </div>
                    <div className="filter-actions">
                        <Button
                            variant="accent"
                            onClick={() => setIsDownloadModalOpen(true)}
                        >
                            <img
                                src={iconDownload}
                                alt=""
                                className="button-icon button-icon-white"
                            />
                            <span>Descargar</span>
                        </Button>
                        {/* <Button variant="primary" onClick={() => setIsShareModalOpen(true)}>
                <img src={iconShare} alt="" />
            </Button> */}
                    </div>
                </div>

                {/* KPIs principales */}
                <div className="panel-section-title">Resumen</div>
                <div className="panel-kpi-row">
                    {(() => {
                        const validaciones = ejemplaresPorValidacion || [];
                        let grabacionesCapturcyl = 0;
                        let ejemplaresCapturcyl = 0;
                        let grabacionesWeb = 0;
                        let ejemplaresWeb = 0;
                        validaciones.forEach((v) => {
                            const nGrabaciones = parseInt(v.total_capturas) || 0;
                            const nEjemplares = parseInt(v.total_ejemplares) || 0;
                            if (v.validacion === "automatica") {
                                grabacionesCapturcyl += nGrabaciones;
                                ejemplaresCapturcyl += nEjemplares;
                            } else {
                                grabacionesWeb += nGrabaciones;
                                ejemplaresWeb += nEjemplares;
                            }
                        });
                        return (
                            <>
                                <div className="panel-kpi-card">
                                    <span className="kpi-label">
                                        Total grabaciones
                                    </span>
                                    <span className="kpi-value">
                                        {formatNumber(grabacionesCapturcyl + grabacionesWeb)}
                                    </span>
                                    <span className="kpi-foot">Periodo seleccionado</span>
                                </div>
                                <div className="panel-kpi-card">
                                    <span className="kpi-label">
                                        Total ejemplares
                                    </span>
                                    <span className="kpi-value">
                                        {formatNumber(ejemplaresCapturcyl + ejemplaresWeb)}
                                    </span>
                                    <span className="kpi-foot">Animales cazados</span>
                                </div>
                                <div className="panel-kpi-card">
                                    <span className="kpi-label">
                                        Total desde CAPTURCYL
                                    </span>
                                    <span className="kpi-value">
                                        {formatNumber(grabacionesCapturcyl)} / {formatNumber(ejemplaresCapturcyl)}
                                    </span>
                                    <span className="kpi-foot">Grabaciones / Ejemplares</span>
                                </div>
                                <div className="panel-kpi-card">
                                    <span className="kpi-label">
                                        Total desde la web
                                    </span>
                                    <span className="kpi-value">
                                        {formatNumber(grabacionesWeb)} / {formatNumber(ejemplaresWeb)}
                                    </span>
                                    <span className="kpi-foot">Grabaciones / Ejemplares</span>
                                </div>
                            </>
                        );
                    })()}
                    {/* {(() => {
                        const especiesCount = new Set(
                            (capturasPorEspecie || [])
                                .filter((e) => visibleSpecies[e.especie_id])
                                .map((e) => e.especie_id),
                        ).size;
                        return (
                            <div className="panel-kpi-card">
                                <span className="kpi-label">Especies con capturas</span>
                                <span className="kpi-value">{formatNumber(especiesCount)}</span>
                                <span className="kpi-foot">Especies distintas registradas</span>
                            </div>
                        );
                    })()} */}
                    {/* {(() => {
                        const validaciones = ejemplaresPorValidacion || [];
                        let totalApp = 0;
                        let total = 0;
                        validaciones.forEach((v) => {
                            const n = parseInt(v.total_ejemplares) || 0;
                            total += n;
                            if (v.validacion === "automatica") totalApp += n;
                        });
                        const pct = total > 0 ? Math.round((totalApp / total) * 100) : 0;
                        return (
                            <div className="panel-kpi-card">
                                <span className="kpi-label">Vía CapturCYL</span>
                                <span className="kpi-value">{pct}%</span>
                                <span className="kpi-foot">Capturas registradas en la app</span>
                            </div>
                        );
                    })()} */}
                </div>

                {/* Capturas por especie - rejilla animales */}
                <div className="panel-section-title">Capturas por especie <span style={{ fontSize: '12px', color: '#6b7280' }}>(capturas/ejemplares)</span></div>
                <Card>
                    <div className="animals-grid">
                        {(() => {
                            const especiesAgrupadas = {};
                            capturasPorEspecie?.forEach((item) => {
                                if (!especiesAgrupadas[item.especie_id]) {
                                    especiesAgrupadas[item.especie_id] = {
                                        especie_id: item.especie_id,
                                        nombre_especie: item.nombre_especie,
                                        total_capturas: 0,
                                        total_ejemplares: 0,
                                    };
                                }
                                especiesAgrupadas[item.especie_id].total_capturas +=
                                    parseInt(item.total_capturas) || 0;
                                especiesAgrupadas[item.especie_id].total_ejemplares +=
                                    parseInt(item.total_ejemplares) || 0;
                            });
                            return Object.values(especiesAgrupadas)
                                .filter((especie) => visibleSpecies[especie.especie_id])
                                .sort((a, b) =>
                                    a.nombre_especie.localeCompare(b.nombre_especie),
                                )
                                .map((especie) => (
                                    <div key={especie.especie_id} className="animal-item">
                                        <img
                                            src={getAnimalIcon(especie.nombre_especie)}
                                            alt={especie.nombre_especie}
                                            className="animal-icon"
                                        />
                                        <div className="animal-info">
                                            <p className="animal-name">
                                                {especie.nombre_especie}
                                            </p>
                                            <p className="animal-value">
                                                {formatNumber(especie.total_capturas)}{" "}
                                                /{" "}
                                                {formatNumber(especie.total_ejemplares)}
                                            </p>
                                        </div>
                                    </div>
                                ));
                        })()}
                    </div>
                </Card>

                {/* Análisis - gráficos */}
                <div className="panel-section-title">Análisis</div>
                <div className="panel-charts-grid">
                    <Card>
                        <div className="card-section">
                            <div className="card-section-header">
                                <p className="card-section-title">
                                    Capturas en el tiempo
                                </p>
                                <div className="card-actions">
                                    <Button
                                        variant="primary"
                                        size="s"
                                        onClick={() => setIsModalOpen(true)}
                                    >
                                        <img src={iconCog} alt="" />
                                    </Button>
                                    <Dropdown
                                        options={periodOptions}
                                        value={selectedPeriod}
                                        onChange={setSelectedPeriod}
                                    />
                                </div>
                            </div>
                            <div
                                className="chart-container-large card-section-chart"
                                style={{ height: "260px" }}
                            >
                                {isLoadingData ? (
                                    <div style={{ color: "var(--dk-green-600)" }}>
                                        Cargando datos...
                                    </div>
                                ) : capturasTemporales.data?.length > 0 ? (
                                    chartType === "lineal" ? (
                                        <Line
                                            data={capturasTemporalesChartData}
                                            options={lineChartOptions}
                                        />
                                    ) : (
                                        <Bar
                                            data={capturasTemporalesChartData}
                                            options={{
                                                ...lineChartOptions,
                                                indexAxis: "x",
                                            }}
                                        />
                                    )
                                ) : (
                                    <div style={{ color: "var(--neutral-500)" }}>
                                        No hay datos disponibles
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>

                    <Card>
                        <div className="card-section">
                            <div className="card-section-header">
                                <p className="card-section-title">
                                    Ejemplares por especie y sexo
                                </p>
                            </div>
                            <div
                                className="chart-container-medium card-section-chart"
                                style={{ height: "260px" }}
                            >
                                {isLoadingData ? (
                                    <div style={{ color: "var(--dk-green-600)" }}>
                                        Cargando datos...
                                    </div>
                                ) : capturasPorEspecie?.length > 0 ? (
                                    <Bar
                                        data={capturasPorEspecieChartData}
                                        options={barChartOptions}
                                    />
                                ) : (
                                    <div style={{ color: "var(--neutral-500)" }}>
                                        No hay datos disponibles
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>

                    <Card>
                        <div className="card-section">
                            <div className="card-section-header">
                                <p className="card-section-title">
                                    Capturas por modalidad
                                </p>
                            </div>
                            <div
                                className="card-pie-chart"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "260px",
                                }}
                            >
                                <div style={{ width: "240px", height: "240px" }}>
                                    {isLoadingData ? (
                                        <div style={{ color: "var(--dk-green-600)" }}>
                                            Cargando...
                                        </div>
                                    ) : tipoAutorizaciones?.length > 0 ? (
                                        <Doughnut
                                            data={tipoAutorizacionesChartData}
                                            options={pieChartOptions}
                                        />
                                    ) : (
                                        <div style={{ color: "var(--neutral-500)" }}>
                                            No hay datos disponibles
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Card>

                    <Card>
                        <div className="card-section">
                            <div className="card-section-header">
                                <p className="card-section-title">
                                    Ejemplares por validación
                                </p>
                            </div>
                            <div
                                className="card-pie-chart"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "260px",
                                }}
                            >
                                <div style={{ width: "240px", height: "240px" }}>
                                    {isLoadingData ? (
                                        <div style={{ color: "var(--dk-green-600)" }}>
                                            Cargando...
                                        </div>
                                    ) : ejemplaresPorValidacion?.length > 0 ? (
                                        <Doughnut
                                            data={ejemplaresPorValidacionChartData}
                                            options={pieChartOptions}
                                        />
                                    ) : (
                                        <div style={{ color: "var(--neutral-500)" }}>
                                            No hay datos disponibles
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Tarjeta: Capturas Semanales por Especie */}
                <div style={{ marginTop: "24px" }}>
                    <Card>
                        <div className="card-content">
                            <div className="card-header">
                                <h3 className="card-title">
                                    Capturas Semanales por Especie
                                </h3>
                            </div>
                            <div
                                className="chart-container-large"
                                style={{ height: "500px", marginTop: "16px" }}
                            >
                                {isLoadingData ? (
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            height: "100px",
                                            color: "var(--dk-green-600)",
                                        }}
                                    >
                                        Cargando...
                                    </div>
                                ) : capturasSemanalPorEspecie?.length > 0 ? (
                                    <Line
                                        data={
                                            capturasSemanalPorEspecieChartData
                                        }
                                        options={capturasSemanalChartOptions}
                                    />
                                ) : (
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            height: "100%",
                                            color: "var(--neutral-500)",
                                        }}
                                    >
                                        No hay datos disponibles
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="page-table-section">
                    <div className="table-section-header">
                        <h3 className="table-section-title">Capturas</h3>
                    </div>
                    <div className="table-filters">
                        <TextField
                            placeholder="Buscar..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            withIcon={true}
                            className="table-search"
                        />
                        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                            <Dropdown
                                options={provincias}
                                value={selectedProvincia}
                                onChange={(value) => {
                                    setSelectedProvincia(value);
                                    setCurrentPage(1);
                                }}
                                placeholder="Todas las provincias"
                                searchable={true}
                                multiple={true}
                            />
                            <Dropdown
                                options={cotos}
                                value={selectedCoto}
                                onChange={(value) => {
                                    setSelectedCoto(value);
                                    setCurrentPage(1);
                                }}
                                placeholder="Todos los cotos"
                                searchable={true}
                                multiple={true}
                            />
                            <Button
                                variant="primary"
                                size="s"
                                style={{ position: 'relative' }}
                                onClick={() => setIsSpeciesModalOpen(true)}
                            >
                                Selecciona las especies
                                {Object.values(visibleSpecies).filter(Boolean).length > 0 && (
                                    <span style={{ position: 'absolute', top: '-8px', right: '-8px', backgroundColor: '#2D6A4F', color: 'white', borderRadius: '50%', width: '22px', height: '22px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', lineHeight: '1', pointerEvents: 'none' }}>
                                        {Object.values(visibleSpecies).filter(Boolean).length}
                                    </span>
                                )}
                            </Button>
                            <Button
                                variant="accent"
                                size="s"
                                onClick={downloadTableExcel}
                                disabled={isDownloadingExcel}
                                title="Descargar tabla en Excel"
                            >
                                {isDownloadingExcel ? (
                                    <span>Descargando...</span>
                                ) : (
                                    <img
                                        src={iconDownload}
                                        alt=""
                                        className="button-icon button-icon-white"
                                    />
                                )}
                            </Button>
                            {permissions?.canViewVisor && (
                                <Button
                                    variant="primary"
                                    size="s"
                                    onClick={downloadImagesZip}
                                    disabled={isDownloadingZip}
                                    title="Descargar imágenes de capturas (ZIP)"
                                >
                                    {isDownloadingZip ? (
                                        <span>Generando ZIP...</span>
                                    ) : (
                                        <>
                                            <img
                                                src={iconDownload}
                                                alt=""
                                                className="button-icon"
                                            />
                                            <span>ZIP imágenes</span>
                                        </>
                                    )}
                                </Button>
                            )}
                        </div>
                        {/* <Button variant="primary" size="s">
                            Filtrar
                        </Button> */}
                    </div>
                    {isLoadingTable ? (
                        <div
                            style={{
                                padding: "40px",
                                textAlign: "center",
                                color: "var(--dk-green-600)",
                            }}
                        >
                            Cargando datos...
                        </div>
                    ) : (
                        <Table
                            columns={columns}
                            data={capturasTable.data || []}
                            onSort={handleSort}
                            sortedColumn={sortBy}
                            sortOrder={sortOrder}
                            onRowClick={handleRowClick}
                        />
                    )}
                    <div className="table-pagination">
                        <Pagination
                            currentPage={capturasTable.current_page || 1}
                            totalPages={capturasTable.last_page || 1}
                            onPageChange={handlePageChange}
                        />
                    </div>
                </div>
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onApply={() => {
                    if (chartSettingsRef.current) {
                        chartSettingsRef.current.apply();
                    }
                    setIsModalOpen(false);
                }}
            >
                <ChartSettings
                    ref={chartSettingsRef}
                    chartType={chartType}
                    onChartTypeChange={setChartType}
                />
            </Modal>

            <Modal
                isOpen={isDownloadModalOpen}
                onClose={() => setIsDownloadModalOpen(false)}
                onApply={handleDownload}
                title="Descargar"
            >
                <DownloadSettings
                    fileType={fileType}
                    onFileTypeChange={setFileType}
                />
            </Modal>

            <Modal
                isOpen={isSpeciesModalOpen}
                onClose={() => setIsSpeciesModalOpen(false)}
                onApply={() => {
                    if (speciesSettingsRef.current) {
                        speciesSettingsRef.current.apply();
                    }
                    setIsSpeciesModalOpen(false);
                }}
            >
                <SpeciesSettings
                    ref={speciesSettingsRef}
                    especies={especies}
                    visibleSpecies={visibleSpecies}
                    onSpeciesChange={setVisibleSpecies}
                />
            </Modal>

            <Modal
                isOpen={isAuthorizationTypeModalOpen}
                onClose={() => setIsAuthorizationTypeModalOpen(false)}
                onApply={() => {
                    if (authorizationTypeSettingsRef.current) {
                        authorizationTypeSettingsRef.current.apply();
                    }
                    setIsAuthorizationTypeModalOpen(false);
                }}
            >
                <AuthorizationTypeSettings
                    ref={authorizationTypeSettingsRef}
                    authorizationTypes={visibleAuthorizationTypes}
                    onAuthorizationTypesChange={setVisibleAuthorizationTypes}
                    chartType={chartType}
                    onChartTypeChange={setChartType}
                    modalidades={modalidades}
                />
            </Modal>

            {/* <Modal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        onApply={() => {
          setIsShareModalOpen(false)
          setIsShareEmailModalOpen(true)
        }}
        title="Compartir"
      >
        <ShareSettings
          shareType={shareType}
          onShareTypeChange={setShareType}
        />
      </Modal> */}

            <Modal
                isOpen={isShareEmailModalOpen}
                onClose={() => setIsShareEmailModalOpen(false)}
                onApply={() => {
                    if (shareEmailModalRef.current) {
                        shareEmailModalRef.current.send();
                    }
                }}
                title="Compartir"
                applyLabel="Enviar"
                cancelLabel="Cancelar"
            >
                <ShareEmailModal
                    ref={shareEmailModalRef}
                    onSend={(email) => {
                        setIsShareEmailModalOpen(false);
                        // TODO: Implementar envío de archivo por email según shareType
                    }}
                    onCancel={() => setIsShareEmailModalOpen(false)}
                />
            </Modal>
        </div>
            )}
        </>
    );
}


